import { Timestamp } from "firebase/firestore";
import { OrganizationId } from "src/types/Organization";

export enum TrainingType {
  CDL_A = "CDL Class A",
  CDL_B = "CDL Class B",
}

export enum CohortTrainingStatus {
  CANCELLED = "cancelled",
  PENDING = "pending",
  ACTIVE = "active",
  COMPLETED = "completed",
}

export enum CohortType {
  PRE_RELEASE = "pre-release",
  POST_RELEASE = "post-release",
}

/** A contract target with just an absolute minimum (the "floor"). */
export interface CohortAbsoluteTarget {
  /** The contracted minimum count for this metric. */
  absoluteTarget: number;
}

/** A contract target with both an absolute floor and a benchmark rate goal. */
export interface CohortRateTarget extends CohortAbsoluteTarget {
  /** Benchmark rate goal, 0–1 (e.g. 0.80 credential, 0.90 placement). */
  percentageGoal: number;
}

/**
 * The cadence parameters of the Emerge/Next Mile forecast model. These differ
 * per contract/jurisdiction (e.g. CA credential waves post one month later than
 * NY), so they live on the cohort rather than being hardcoded. Omit to fall
 * back to the NY defaults (wave lags 2/3, 0.75 wave-1 share, 6 offer tranches).
 */
export interface CohortForecastModel {
  /** Enrollment window length in months (W). Default 12. */
  windowMonths: number;
  /** Contract-month lag of the first credential wave (NY 2, CA 3). */
  credentialWave1Lag: number;
  /** Contract-month lag of the second credential wave (NY 3, CA 4). */
  credentialWave2Lag: number;
  /** Share of a cohort's credentials in wave 1 (rest in wave 2). Default 0.75. */
  credentialWave1Share: number;
  /** Number of monthly offer tranches per credential wave. Default 6. */
  offerTranches: number;
  /** Month lag of the first offer tranche after a credential. Default 1. */
  firstTrancheLag: number;
}

/**
 * Contract minimums for a cohort's SOW year and the inputs to the Emerge/Next
 * Mile forecast model. `enrollments.absoluteTarget` is the enrollment floor (E);
 * the credential and employment blocks carry both their absolute floors (C, J)
 * and the benchmark rate goals that drive the forecast cadence. `forecast`
 * carries the per-cohort cadence params (NY vs CA differ). Optional per cohort
 * because only contracted cohorts (e.g. MOCJ FY26/FY27) carry targets.
 */
export interface CohortContractTargets {
  enrollments: CohortAbsoluteTarget;
  credentials: CohortRateTarget;
  employment: CohortRateTarget;
  /** Forecast cadence model; omit for the NY defaults. */
  forecast?: CohortForecastModel;
}

export type Cohort = {
  uid: string;
  name: string;
  startDate: string;
  startDateFirestoreTimestamp: Timestamp;
  trainingType: TrainingType;
  status: CohortTrainingStatus;
  cohortType: CohortType;
  organizationId: OrganizationId;
  /**
   * Contract minimums + forecast inputs for this cohort's SOW year. Present
   * only for contracted cohorts; drives the impact report's targets and the
   * forecast-vs-actual view.
   */
  contractTargets?: CohortContractTargets;
  /**
   * ISO date the enrollment window opens (contract month 1). Falls back to
   * `startDate` when unset. Used to place live counts on the forecast curve.
   */
  enrollmentWindowStartDate?: string;
  /**
   * Partner account auto-assigned to users joining this cohort.
   * Used for funders/sponsors (e.g., DOC) who need visibility into participants.
   * Only applied if user doesn't already have a referredByPartnerId.
   */
  defaultPartnerId?: string;
  /**
   * Maximum number of students that can be enrolled and billed for this cohort.
   * Used for billing capacity tracking to prevent over-billing.
   */
  maximumSeats?: number;
};
